/**
 * Compositor del canvas final — 3 capas estrictas:
 *   Capa 1 (abajo):  color/imagen de fondo de la plantilla
 *   Capa 2 (medio):  selfie procesada (fondo removido) con su matriz de transformación
 *   Capa 3 (arriba): marco + marca de agua según reglas del backend
 *
 * La exportación es 100% client-side: canvas.toDataURL(), nada sube al servidor.
 */
import type { SelfieTransform, TemplateConfig, WatermarkPosition } from './types';

export const CANVAS_WIDTH = 1080;
export const CANVAS_HEIGHT = 1440; // 3:4 — formato retrato

const imageCache = new Map<string, Promise<HTMLImageElement>>();

export function loadImage(url: string): Promise<HTMLImageElement> {
	let cached = imageCache.get(url);
	if (!cached) {
		cached = new Promise((resolve, reject) => {
			const img = new Image();
			// Necesario para poder exportar el canvas (los assets de R2 deben servir CORS)
			img.crossOrigin = 'anonymous';
			img.onload = () => resolve(img);
			img.onerror = () => reject(new Error(`No se pudo cargar el asset: ${url}`));
			img.src = url;
		});
		imageCache.set(url, cached);
	}
	return cached;
}

/** Dibuja una imagen cubriendo todo el canvas (object-fit: cover). */
function drawCover(ctx: CanvasRenderingContext2D, img: HTMLImageElement) {
	const canvasRatio = CANVAS_WIDTH / CANVAS_HEIGHT;
	const imageRatio = img.width / img.height;
	let width = CANVAS_WIDTH;
	let height = CANVAS_HEIGHT;

	if (imageRatio > canvasRatio) {
		width = CANVAS_HEIGHT * imageRatio;
	} else {
		height = CANVAS_WIDTH / imageRatio;
	}

	ctx.drawImage(img, (CANVAS_WIDTH - width) / 2, (CANVAS_HEIGHT - height) / 2, width, height);
}

function watermarkOrigin(
	position: WatermarkPosition,
	width: number,
	height: number,
	padding: number
): { x: number; y: number } {
	const [vertical, horizontal] =
		position === 'center' ? ['center', 'center'] : position.split('-');

	let x = (CANVAS_WIDTH - width) / 2;
	if (horizontal === 'left') x = padding;
	else if (horizontal === 'right') x = CANVAS_WIDTH - width - padding;

	let y = (CANVAS_HEIGHT - height) / 2;
	if (vertical === 'top') y = padding;
	else if (vertical === 'bottom') y = CANVAS_HEIGHT - height - padding;

	return { x, y };
}

export async function drawComposite(
	canvas: HTMLCanvasElement,
	config: TemplateConfig,
	selfie: HTMLCanvasElement | null,
	transform: SelfieTransform
): Promise<void> {
	canvas.width = CANVAS_WIDTH;
	canvas.height = CANVAS_HEIGHT;
	const ctx = canvas.getContext('2d')!;

	// ── Capa 1: fondo ────────────────────────────────────────────────────────
	ctx.fillStyle = config.background?.color || '#ffffff';
	ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);

	if (config.background?.image_url) {
		try {
			drawCover(ctx, await loadImage(config.background.image_url));
		} catch {
			// El color de fondo ya cubre el canvas como fallback
		}
	}

	// ── Capa 2: selfie procesada ────────────────────────────────────────────
	if (selfie) {
		const baseScale = Math.min(CANVAS_WIDTH / selfie.width, CANVAS_HEIGHT / selfie.height);
		const scale = baseScale * transform.scale;

		ctx.save();
		ctx.translate(CANVAS_WIDTH / 2 + transform.x, CANVAS_HEIGHT / 2 + transform.y);
		ctx.rotate((transform.rotation * Math.PI) / 180);
		ctx.scale(scale, scale);
		ctx.drawImage(selfie, -selfie.width / 2, -selfie.height / 2);
		ctx.restore();
	}

	// ── Capa 3: marco + marca de agua ───────────────────────────────────────
	if (config.frame?.url) {
		try {
			drawCover(ctx, await loadImage(config.frame.url));
		} catch {
			// Marco opcional: se ignora si falla
		}
	}

	const watermark = config.watermark;
	if (watermark?.url) {
		try {
			const logo = await loadImage(watermark.url);
			const width = (CANVAS_WIDTH * watermark.size_percent) / 100;
			const height = width * (logo.height / logo.width);
			const { x, y } = watermarkOrigin(watermark.position, width, height, watermark.padding_px);

			ctx.save();
			ctx.globalAlpha = watermark.opacity;
			ctx.drawImage(logo, x, y, width, height);
			ctx.restore();
		} catch {
			// Sin marca de agua si el asset no carga
		}
	}
}

/** Exportación zero-server: descarga instantánea vía data URL. */
export function downloadCanvas(canvas: HTMLCanvasElement, fileName = 'selfie-studio.png') {
	const link = document.createElement('a');
	link.download = fileName;
	link.href = canvas.toDataURL('image/png');
	link.click();
}
